import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    FiCheck,
    FiAward,
    FiUsers,
    FiThumbsUp,
    FiArrowRight,
    FiShield,
    FiClock,
    FiCalendar,
    FiUser
} from 'react-icons/fi';
import {
    HiOutlineOfficeBuilding,
    HiOutlineHome,
    HiOutlineCog,
    HiOutlineSparkles,
    HiOutlineClipboardCheck,
    HiOutlineShieldCheck,
    HiOutlineAcademicCap,
    HiOutlineShoppingBag,
    HiOutlineHeart,
    HiOutlineLibrary,
    HiOutlineTruck
} from 'react-icons/hi';
import SectionHeading from '../components/SectionHeading';
import ServiceCard from '../components/ServiceCard';
import SEO from '../components/SEO';

const heroSlides = [
    {
        image: "/images/site/hero-facility-team.png",
        key: "slide1"
    },
    {
        image: "/images/site/hero-rope-access.jpg",
        key: "slide2"
    },
    {
        image: "/images/site/commercial-cleaning.jpg",
        key: "slide3"
    },
    {
        image: "/images/site/hero-facility-support-new.png",
        key: "slide4"
    },
    {
        image: "/images/site/hero-residential-cleaning-new.png",
        key: "slide5"
    }
];

const CountUp = ({ end, duration = 2, suffix = "+" }) => {
    const [count, setCount] = useState(0);
    const nodeRef = useRef(null);
    const inView = useInView(nodeRef, { once: true, margin: "-100px" });

    useEffect(() => {
        if (inView) {
            const endValue = parseInt(end);
            const startTime = Date.now();
            const animate = () => {
                const now = Date.now();
                const progress = Math.min((now - startTime) / (duration * 1000), 1);
                const ease = 1 - Math.pow(1 - progress, 4);

                setCount(Math.floor(ease * endValue));

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    setCount(endValue);
                }
            };
            requestAnimationFrame(animate);
        }
    }, [inView, end, duration]);

    return <span ref={nodeRef}>{count}{suffix}</span>;
};

const Home = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language ? i18n.language.startsWith('ar') : false;
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev + 1) % heroSlides.length);
        }, 5500);
        return () => clearInterval(interval);
    }, []);

    const services = [
        {
            icon: HiOutlineOfficeBuilding,
            title: t('home.services.items.commercial.title'),
            description: t('home.services.items.commercial.desc'),
        },
        {
            icon: HiOutlineHome,
            title: t('home.services.items.residential.title'),
            description: t('home.services.items.residential.desc'),
        },
        {
            icon: HiOutlineCog,
            title: t('home.services.items.industrial.title'),
            description: t('home.services.items.industrial.desc'),
        },
        {
            icon: HiOutlineSparkles,
            title: t('home.services.items.window.title'),
            description: t('home.services.items.window.desc'),
        },
        {
            icon: HiOutlineClipboardCheck,
            title: t('home.services.items.floor.title'),
            description: t('home.services.items.floor.desc'),
        },
        {
            icon: HiOutlineShieldCheck,
            title: t('home.services.items.deep.title'),
            description: t('home.services.items.deep.desc'),
        },
    ];

    const whyChooseUs = [
        {
            icon: FiAward,
            title: t('home.whyUs.items.trained.title'),
            description: t('home.whyUs.items.trained.desc'),
        },
        {
            icon: FiShield,
            title: t('home.whyUs.items.staff.title'),
            description: t('home.whyUs.items.staff.desc'),
        },
        {
            icon: FiClock,
            title: t('home.whyUs.items.quality.title'),
            description: t('home.whyUs.items.quality.desc'),
        },
        {
            icon: FiThumbsUp,
            title: t('home.whyUs.items.satisfaction.title'),
            description: t('home.whyUs.items.satisfaction.desc'),
        },
    ];

    const stats = [
        { number: 50, suffix: '+', label: t('home.stats.professionals') },
        { number: 15, suffix: '+', label: t('home.stats.experience') },
        { number: 10, suffix: '+', label: t('home.stats.clients') },
        { number: 20, suffix: '+', label: t('home.stats.projects') },
        { number: 20, suffix: '+', label: t('home.stats.satisfied') },
    ];

    const industries = [
        { icon: HiOutlineOfficeBuilding, title: t('home.industries.items.offices') },
        { icon: HiOutlineShoppingBag, title: t('home.industries.items.malls') },
        { icon: HiOutlineHome, title: t('home.industries.items.residential') },
        { icon: HiOutlineHeart, title: t('home.industries.items.healthcare') },
        { icon: HiOutlineAcademicCap, title: t('home.industries.items.education') },
        { icon: HiOutlineSparkles, title: t('home.industries.items.hospitality') },
        { icon: HiOutlineTruck, title: t('home.industries.items.industrial') },
        { icon: HiOutlineLibrary, title: t('home.industries.items.government') },
    ];

    const featuredBlogs = [
        {
            id: 1,
            title: t('blogs.fallback.post1.title'),
            excerpt: t('blogs.fallback.post1.excerpt'),
            category: t('blogs.fallback.post1.category'),
            readTime: t('blogs.fallback.post1.readTime'),
            image: "/images/site/blog-commercial.png"
        },
        {
            id: 2,
            title: t('blogs.fallback.post2.title'),
            excerpt: t('blogs.fallback.post2.excerpt'),
            category: t('blogs.fallback.post2.category'),
            readTime: t('blogs.fallback.post2.readTime'),
            image: "/images/site/blog-hiddencosts.png"
        },
        {
            id: 3,
            title: t('blogs.fallback.post3.title'),
            excerpt: t('blogs.fallback.post3.excerpt'),
            category: t('blogs.fallback.post3.category'),
            readTime: t('blogs.fallback.post3.readTime'),
            image: "/images/site/blog-ropeaccess.png"
        }
    ];

    return (
        <div className="w-full">
            <SEO
                title={t('seo.home.title')}
                description={t('seo.home.description')}
                keywords="professional cleaning bahrain, commercial cleaning bahrain, facility support bahrain, rope access window cleaning, marble polishing bahrain"
            />

            {/* 1. Hero Section with Slideshow */}
            <section className="relative min-h-screen flex items-center">
                {/* Background Slideshow */}
                <div className="absolute inset-0 z-0">
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={currentSlide}
                            src={heroSlides[currentSlide].image}
                            alt="PrimeForce Facility Services"
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1.2, ease: "easeInOut" }}
                            className="w-full h-full object-cover"
                        />
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-gradient-to-r from-gray-950/85 via-gray-900/75 to-gray-950/85"></div>
                </div>

                {/* Content */}
                <div className="container-custom relative z-10 py-28 md:py-36">
                    <div className="max-w-7xl mx-auto text-center">
                        <motion.span
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-block px-5 py-2 bg-accent-500/20 text-accent-400 rounded-full text-sm font-semibold mb-6 border border-accent-500/30 backdrop-blur-sm whitespace-nowrap"
                        >
                            {t('home.hero.badge')}
                        </motion.span>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-2xl sm:text-3xl md:text-4xl lg:text-[2.5rem] xl:text-[3.2rem] font-bold text-white mb-6 leading-tight whitespace-normal lg:whitespace-nowrap"
                        >
                            {t('home.hero.titleStart')} <span className="text-accent-400">{t('home.hero.titleAccent')}</span> {t('home.hero.titleMid')} <span className="text-primary-400">{t('home.hero.titlePrimary')}</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="text-base sm:text-lg md:text-xl text-gray-200 mb-10 leading-relaxed max-w-5xl mx-auto"
                        >
                            {t('home.hero.description')}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            className="flex flex-wrap gap-4 justify-center"
                        >
                            <Link to="/contact" className="btn-accent text-lg px-8 py-4">
                                {t('home.hero.ctaPrimary')}
                                <FiArrowRight className="ml-2 inline rtl:rotate-180" />
                            </Link>
                            <Link to="/services" className="btn-secondary border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-4">
                                {t('home.hero.ctaSecondary')}
                            </Link>
                        </motion.div>
                    </div>
                </div>

                {/* Slide Indicators */}
                <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
                    {heroSlides.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentSlide(index)}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-accent-500 w-8' : 'bg-white/50 hover:bg-white'}`}
                            aria-label={`Go to slide ${index + 1}`}
                        ></button>
                    ))}
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10"
                >
                    <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                        <div className="w-1.5 h-3 bg-white/70 rounded-full mt-2"></div>
                    </div>
                </motion.div>
            </section>

            {/* 2. Trust Statistics Section */}
            <section className="py-12 bg-primary-600 text-white shadow-xl relative z-20">
                <div className="container-custom">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.08 }}
                                className="p-4"
                            >
                                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-accent-400 mb-1">
                                    <CountUp end={stat.number} suffix={stat.suffix} />
                                </div>
                                <div className="text-white/90 text-sm md:text-base font-medium">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. About PrimeForce Section (NEW HOMEPAGE SECTION) */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-4">
                                {t('home.aboutSection.subtitle')}
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                                {t('home.aboutSection.title')}
                            </h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-8">
                                {t('home.aboutSection.description')}
                            </p>
                            <Link to="/about" className="btn-primary">
                                {t('home.aboutSection.cta')} →
                            </Link>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img
                                    src="/images/site/facility-support.jpg"
                                    alt="PrimeForce Services Team"
                                    loading="lazy"
                                    className="w-full h-[400px] object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-accent-500 text-white p-6 rounded-2xl shadow-xl hidden md:block rtl:-right-6 rtl:left-auto">
                                <div className="text-3xl font-bold">15+</div>
                                <div className="text-sm text-accent-100">{t('home.stats.experience')}</div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 4. Professional Services Section */}
            <section className="section-padding bg-gray-50">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('home.services.subtitle')}
                        title={t('home.services.title')}
                        description={t('home.services.description')}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <ServiceCard
                                key={service.title}
                                icon={service.icon}
                                title={service.title}
                                description={service.description}
                                index={index}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* 5. Why Choose Us Section */}
            <section className="section-padding relative overflow-hidden bg-gradient-to-br from-primary-700 to-primary-900 text-white">
                <div className="container-custom relative z-10">
                    <SectionHeading
                        subtitle={t('home.whyUs.subtitle')}
                        title={t('home.whyUs.title')}
                        description={t('home.whyUs.description')}
                        light={true}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {whyChooseUs.map((item, index) => (
                            <motion.div
                                key={item.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-all duration-300 group border border-white/10"
                            >
                                <div className="w-16 h-16 mx-auto mb-4 bg-accent-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <item.icon className="text-white" size={28} />
                                </div>
                                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                                <p className="text-white/80 text-sm leading-relaxed">{item.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 6. Industries Served Section (NEW SECTION) */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('home.industries.subtitle')}
                        title={t('home.industries.title')}
                        description={t('home.industries.description')}
                    />

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-6">
                        {industries.map((ind, index) => (
                            <motion.div
                                key={ind.title}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 text-center hover:shadow-lg hover:border-primary-200 transition-all group"
                            >
                                <div className="w-14 h-14 mx-auto mb-3 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center group-hover:bg-primary-500 group-hover:text-white transition-colors">
                                    <ind.icon size={28} />
                                </div>
                                <h4 className="font-semibold text-gray-800 text-sm md:text-base">{ind.title}</h4>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7. Client Logos / Trusted Across Bahrain Section */}
            <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-100">
                <div className="container-custom">
                    <h3 className="text-center text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-widest mb-6">
                        {t('home.trustedAcross')}
                    </h3>
                    <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 md:gap-12 opacity-80 transition-all">
                        <div className="font-bold text-xs sm:text-sm md:text-lg text-gray-700 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">{t('home.trustedItems.offices')}</div>
                        <div className="font-bold text-xs sm:text-sm md:text-lg text-gray-700 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">{t('home.trustedItems.malls')}</div>
                        <div className="font-bold text-xs sm:text-sm md:text-lg text-gray-700 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">{t('home.trustedItems.healthcare')}</div>
                        <div className="font-bold text-xs sm:text-sm md:text-lg text-gray-700 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">{t('home.trustedItems.residential')}</div>
                        <div className="font-bold text-xs sm:text-sm md:text-lg text-gray-700 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">{t('home.trustedItems.logistics')}</div>
                    </div>
                </div>
            </section>

            {/* 8. Latest Insights / Blog Preview */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <SectionHeading
                        subtitle={t('blogs.hero.badge')}
                        title={t('blogs.hero.titleStart') + " " + t('blogs.hero.titleAccent')}
                        description={t('blogs.hero.subtext')}
                    />

                    <div className="grid md:grid-cols-3 gap-8">
                        {featuredBlogs.map((blog, index) => {
                            const activeTitle = isArabic ? t(`blogs.fallback.post${blog.id}.title`) : blog.title;
                            const activeExcerpt = isArabic ? t(`blogs.fallback.post${blog.id}.excerpt`) : blog.excerpt;
                            const activeCategory = isArabic ? t(`blogs.fallback.post${blog.id}.category`) : blog.category;

                            return (
                                <motion.article
                                    key={blog.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all group flex flex-col justify-between"
                                >
                                    <Link to={`/blogs/${blog.id}`} className="block h-full flex flex-col justify-between">
                                        <div>
                                            <div className="relative h-48 overflow-hidden">
                                                <span className="absolute top-4 start-4 z-10 bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                                                    {activeCategory}
                                                </span>
                                                <img
                                                    src={blog.image}
                                                    alt={activeTitle}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                />
                                            </div>
                                            <div className="p-6">
                                                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-primary-500 transition-colors line-clamp-2">
                                                    {activeTitle}
                                                </h3>
                                                <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                                                    {activeExcerpt}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="px-6 pb-6 pt-0">
                                            <span className="inline-flex items-center gap-2 text-primary-600 font-semibold group-hover:gap-3 transition-all text-sm">
                                                <span>{t('blogs.readMore')}</span>
                                                <FiArrowRight className="rtl:rotate-180 flex-shrink-0" />
                                            </span>
                                        </div>
                                    </Link>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* 9. CTA Section */}
            <section className="relative py-20 bg-gray-900 text-white">
                <div className="absolute inset-0 opacity-30">
                    <img
                        src="/images/site/commercial-cleaning.jpg"
                        alt="Facility Services"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="container-custom relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                            {t('home.cta.titleStart')} <span className="text-accent-400">{t('home.cta.titleAccent')}</span> {t('home.cta.titleEnd')}
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                            {t('home.cta.description')}
                        </p>
                        <Link to="/contact" className="btn-accent text-lg px-10 py-4">
                            {t('home.cta.button')}
                            <FiArrowRight className="ml-2 inline rtl:rotate-180" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Home;
